import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Menu, X, Volume2, VolumeX } from 'lucide-react';
import { cn } from '@/lib/utils';

const navLinks = [
  { name: 'Drops', href: '#drops' },
  { name: 'Worlds', href: '#worlds' },
  { name: 'Listen', href: '#listen' },
  { name: 'Universe', href: '#universe' },
];

export const Navigation = ({ isAudioEnabled, onToggleAudio }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-600 ease-smooth',
          isScrolled
            ? 'bg-background/90 backdrop-blur-xl border-b border-crimson/10'
            : 'bg-transparent'
        )}
      >
        <nav className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <motion.a
              href="#"
              className="relative z-10 flex items-center gap-3 group"
              whileHover={{ scale: 1.02 }}
            >
              <div className="relative">
                <div className="w-10 h-10 bg-crimson flex items-center justify-center animate-flicker">
                  <span className="font-display text-background text-xl font-bold">S</span>
                </div>
                <div className="absolute inset-0 bg-crimson blur-lg opacity-40 group-hover:opacity-60 transition-opacity" />
              </div>
              <div className="hidden sm:block">
                <span className="font-display text-xl text-ivory tracking-wider">SUGO</span>
                <span className="font-display text-xl text-crimson ml-1 tracking-wider">SOUND</span>
              </div>
            </motion.a>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-10">
              {navLinks.map((link, index) => (
                <motion.a
                  key={link.name}
                  href={link.href}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 + 0.5 }}
                  className="relative text-xs font-body uppercase tracking-[0.25em] text-muted-foreground hover:text-crimson transition-colors duration-400 group"
                >
                  {link.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-px bg-crimson group-hover:w-full transition-all duration-400" />
                </motion.a>
              ))}
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-3">
              {/* Audio Toggle */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={onToggleAudio}
                className={cn(
                  "hidden sm:flex w-10 h-10 items-center justify-center border transition-all duration-400",
                  isAudioEnabled
                    ? "border-crimson/50 text-crimson bg-crimson/10"
                    : "border-border/50 text-muted-foreground hover:border-crimson/30 hover:text-crimson"
                )}
              >
                {isAudioEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </motion.button>

              {/* CTA */}
              <Button variant="neonOutline" size="sm" className="hidden sm:flex" asChild>
                <a href="#drops">Enter</a>
              </Button>

              {/* Mobile Menu */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden w-10 h-10 flex items-center justify-center text-ivory"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </nav>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 lg:hidden"
          >
            <div className="absolute inset-0 bg-background/98 backdrop-blur-xl" />
            {/* Scanlines */}
            <div className="absolute inset-0 scanlines" />
            <nav className="relative h-full flex flex-col items-center justify-center gap-8">
              {navLinks.map((link, index) => (
                <motion.a
                  key={link.name}
                  href={link.href}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="font-display text-4xl text-ivory hover:text-crimson transition-colors duration-400"
                >
                  {link.name}
                </motion.a>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-8"
              >
                <Button variant="neon" size="lg" asChild>
                  <a href="#universe">Enter Universe</a>
                </Button>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
